////@ts-check

import { Button, Dialog, makeStyles } from "@mui/material";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { useQuery } from "react-query";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";

import { getMatchById } from "../config/mainRequest";
import { useSelector } from "react-redux";
import { FlatList, Text } from "react-native";

/**
 * @typedef QCModalProps
 * @prop {boolean} modalIsOpen
 * @prop {React.Dispatch<React.SetStateAction<boolean>>} setIsQcModalOpen
 *
 */

/**
 *
 * @param {QCModalProps} props
 */

const QCModal = ({ modalIsOpen, setIsQcModalOpen, matchId }) => {
  const reduxState = useSelector((state) => state);
  const [matchString, setMatchString] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("theme-dark")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("theme-dark"));
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const [finalError, setFinalError] = useState([]);
  const {
    data: matchData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryFn: ["matchById", matchId],
    queryFn: () =>
      getMatchById({ match_id: matchId, token: reduxState?.auth?.token }),
    enabled: modalIsOpen,
  });

  useEffect(() => {
    const str = matchData?.match_sense_data?.match_sense?.match_string;
    setMatchString([]);
    if (str) {
      setMatchString(str.split(","));
    }
  }, [matchData, modalIsOpen]);

  const cornerAnalyzerHelper = (str) => {
    const validKeys = new Set(["SP", "LP", "C", "TB"]);
    const validStartingPosition = new Set(["1", "71"]);
    if (str) {
      const arr = str.split("-");
      // console.log(arr);
      if (validKeys.has(arr[2])) {
        if (validStartingPosition.has(arr[4]) && arr[8] !== "CN") {
          return "Possible Corner";
        }
      }
    }
    return "";
  };

  function timeToSeconds(time) {
    const [hours, minutes, seconds] = time.split(":").map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  }

  const passAnalyzerHelper = (str, index) => {
    const validShots = new Set(["CS", "LS", "H"]);
    const validPass = new Set(["XSP", "XLP", "XC", "XTB"]);

    const arr = str.split("-");
    const currentPlayer = arr[0] + arr[1];
    const currentTimeStamp = arr[6];
    const currentTimeInSeconds = timeToSeconds(currentTimeStamp);
    const prevIndex = index - 1;
    const currentTeam = arr[0];
    const currentShotNotation = arr[3];

    if (validShots.has(arr[2]) && prevIndex >= 0 && arr[8] === "X") {
      for (let i = prevIndex; i >= 0; i--) {
        const arr = matchString[i].split("-");
        if (
          arr[0] !== currentTeam &&
          arr[2].charAt(0) !== "X" &&
          parseInt(arr[3]) > 0
        ) {
          break;
        }
        if (arr[0] + arr[1] === currentPlayer) {
          if (
            validPass.has(arr[2]) &&
            currentShotNotation !== "4" &&
            arr[3] !== "2"
          ) {
            return "Possible Key Pass";
          }
          if (
            validPass.has(arr[2]) &&
            currentShotNotation === "4" &&
            arr[3] !== "3"
          ) {
            return "Possible Assist";
          }
        }
      }
    }
    return "";
  };

  const freeKickAnalyzerHelper = (str, index) => {
    const validKicks = new Set(["FK", "PK"]);
    const validFouls = new Set(["F", "YC", "RC"]);
    const validFoulAttribute = new Set(["OFF", "HB"]);

    const arr = str.split("-");
    const prevIndex = index - 1;
    const currentTeam = arr[0];

    if (validKicks.has(arr[8]) && prevIndex >= 0) {
      for (let i = prevIndex; i >= 0; i--) {
        const arr = matchString[i].split("-");
        if (arr[0] !== currentTeam && arr[2].charAt(0) !== "X") {
          if (!(validFouls.has(arr[8]) || validFoulAttribute.has(arr[2]))) {
            return "Possible Missed Foul";
          }
          break;
        }
      }
    }
    return "";
  };

  const foulAnalyzerHelper = (str, index) => {
    const validKicks = new Set(["FK", "PK"]);
    const validFouls = new Set(["F", "YC", "RC"]);
    const validFoulAttribute = new Set(["OFF", "HB"]);
    const validDuels = new Set(["AD", "GD"]);

    const arr = str.split("-");
    const postIndex = index + 1;
    const currentTeam = arr[0];
    const matchLength = matchString?.length;

    if (
      (validFouls.has(arr[8]) || validFoulAttribute.has(arr[2])) &&
      postIndex < matchLength
    ) {
      for (let i = postIndex; i <= matchLength; i++) {
        const arr = matchString[i].split("-");
        if (
          arr[0] !== currentTeam &&
          arr[2].charAt(0) !== "X" &&
          !validDuels.has(arr[2])
        ) {
          if (!validKicks.has(arr[8])) {
            return "Possible Missed Free Kick";
          }
          break;
        }
      }
    }
    return "";
  };

  const goalKickAnalyzerHelper = (str) => {
    const validPenaltyBox = new Set(["40", "50"]);
    const arr = str.split("-");
    if (arr[8] === "GK" && !validPenaltyBox.has(arr[4])) {
      return "Goal kick tagged on wrong location";
    }
    return "";
  };

  const timeStampAnalyzer10sHelper = (str, index) => {
    const arr = str.split("-");
    const postIndex = index + 1;
    const currentTimeStamp = arr[6];
    const currentTimeInSeconds = timeToSeconds(currentTimeStamp);
    if (postIndex < matchString?.length) {
      const newArr = matchString[postIndex].split("-");
      const timeDifference = timeToSeconds(newArr[6]) - currentTimeInSeconds;
      if (timeDifference > 10) {
        return "Next Action Tagged after 10s";
      }
    }
    return "";
  };

  const repetitiveTimeStampAnalyzerHelper = (str, index) => {
    const arr = str.split("-");
    const currentTimeStamp = arr[6];
    const postIndex = index + 1;
    let counter = 0;

    if (postIndex + 3 < matchString?.length) {
      for (let i = postIndex; i <= postIndex + 3; i++) {
        const arr = matchString[i].split("-");
        if (arr[6] === currentTimeStamp) counter++;
      }
      if (counter === 3) return "Repetitive timestamp";
    }
    return "";
  };

  const masterAnalyzer = (str, index) => {
    const finalList = [];
    const s1 = cornerAnalyzerHelper(str);
    const s2 = passAnalyzerHelper(str, index);
    const s3 = freeKickAnalyzerHelper(str, index);
    const s4 = foulAnalyzerHelper(str, index);
    const s5 = goalKickAnalyzerHelper(str);
    const s6 = timeStampAnalyzer10sHelper(str, index);
    const s7 = repetitiveTimeStampAnalyzerHelper(str, index);
    finalList.push(s1);
    finalList.push(s2);
    finalList.push(s3);
    finalList.push(s4);
    finalList.push(s5);
    finalList.push(s6);
    finalList.push(s7);
    return finalList;
  };

  const renderError = () => {
    const finalArr = [];
    matchString.forEach((item, index) => {
      finalArr.push(masterAnalyzer(item, index));
    });
    setFinalError(finalArr);
  };

  return (
    <Modal
      isOpen={modalIsOpen}
      style={{
        overlay: { backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1000 },
        content: {
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-1)",
          borderRadius: "12px",
          padding: "20px",
          maxWidth: "95vw",
          maxHeight: "95vh",
          margin: "auto",
        },
      }}
    >
      <ScrollSync>
        <div
          style={{
            height: "100%",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "3fr 1fr 5fr",
            gap: "1rem",
          }}
        >
          <div
            style={{
              border: "1px solid var(--border-1)",
              padding: "0.5rem",
              borderRadius: "0.5rem",
              backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            }}
          >
            <p
              style={{
                width: "99%",
                height: "85vh",
                color: "var(--text-1)",
                margin: 0,
                padding: 0,
                overflow: "auto",
                fontFamily: "monospace",
                fontSize: "13px",
              }}
            >
              {!isLoading && !isRefetching
                ? matchData?.match_sense_data?.match_sense?.match_string
                : "loading..."}
            </p>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "3rem",
            }}
          >
            <Button
              onClick={() => renderError()}
              variant="contained"
              size="large"
              color="primary"
              disabled={
                !matchString ||
                matchString?.length === 0 ||
                isLoading ||
                isRefetching
              }
            >
              Run QC
            </Button>
            <Button
              onClick={() => setIsQcModalOpen && setIsQcModalOpen(false)}
              variant="outlined"
              size="large"
              color="primary"
            >
              Close
            </Button>
          </div>
          <div
            style={{
              border: "1px solid var(--border-1)",
              padding: "0.5rem",
              borderRadius: "0.5rem",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.5rem",
              backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            }}
          >
            <ScrollSyncPane>
              <div
                style={{
                  border: "1px solid var(--border-1)",
                  padding: "0.5rem",
                  borderRadius: "0.5rem",
                  height: "85vh",
                  overflow: "auto",
                }}
              >
                {matchString?.map((item, index) => {
                  return (
                    <div key={index.toString()} style={{ maxHeight: "2rem" }}>
                      {/* <div
                        style={{
                          width: "100%",
                          height: 0.5,
                          background: "#000",
                        }}
                      /> */}
                      <textarea
                        style={{
                          resize: "none",
                          border: "none",
                          outline: "none",
                          width: "100%",
                          height: "2rem",
                          textAlign: "center",
                          background:
                            index % 2 === 0 ? "var(--surface-2)" : "var(--surface-1)",
                          color: "var(--text-1)",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                        value={
                          !isLoading && !isRefetching ? item : "loading..."
                        }
                        onChange={(e) => {
                          const newArr = [...matchString];
                          newArr[index] = e.target.value;
                          setMatchString(newArr);
                        }}
                      />
                      {/* <div
                        style={{
                          width: "100%",
                          height: 0.5,
                          background: "#000",
                        }}
                      /> */}
                    </div>
                  );
                })}
              </div>
            </ScrollSyncPane>
            <ScrollSyncPane>
              <div
                style={{
                  border: "1px solid var(--border-1)",
                  padding: "0.5rem",
                  borderRadius: "0.5rem",
                  height: "85vh",
                  overflow: "hidden",
                }}
              >
                {finalError.length === 0 ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <h3 style={{ color: "var(--text-1)" }}>Run QC</h3>
                  </div>
                ) : (
                  finalError?.map((item, index) => {
                    return (
                      <div
                        key={index.toString()}
                        style={{
                          maxHeight: "2rem",
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                          height: "2rem",
                          width: "100%",
                          background:
                            index % 2 === 0 ? "var(--surface-2)" : "var(--surface-1)",
                          color: "var(--text-1)",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                      >
                        {item?.map((error, index) => (
                          <div
                            key={index.toString()}
                            style={{
                              // height: "2rem",
                              display: "flex",
                              flexDirection: "row",
                              padding: 0,
                              margin: 0,
                            }}
                          >
                            <p
                              style={{
                                // color: `rgba(${Math.floor(
                                //   Math.random() * 256
                                // )}, ${Math.floor(
                                //   Math.random() * 256
                                // )}, ${Math.floor(Math.random() * 256)}, 1)`,
                                // textAlign: "center",
                                // backgroundColor: "red",
                                color: "tomato",
                              }}
                            >
                              {error}
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollSyncPane>
          </div>
        </div>
      </ScrollSync>
    </Modal>
  );
};

export default QCModal;
